<script setup>
import ApplicationLogo from '@/components/ApplicationLogo.vue';
import Button from '@/components/ui/button/Button.vue';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Toaster from '@/components/ui/toast/Toaster.vue';
import { useToast } from '@/components/ui/toast/use-toast';
import { useDarkModeStore } from '@/stores/dark-mode';
import { usePage } from '@inertiajs/vue3';
import { Check, LogOut, MonitorCog, Moon, Sun } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';

const { toast } = useToast();
const page = usePage();
const { changeThemeUser } = useDarkModeStore();
const { darkMode } = storeToRefs(useDarkModeStore());
watch(
    () => ({ ...page.props.flash }), // Proxy'yi düz bir nesneye çeviriyoruz
    (value) => {
        if (value.error && value.error.trim() !== '') {
            toast({
                variant: 'destructive',
                title: 'Hata',
                description: value.error,
            });
        } else if (value.success && value.success.trim() !== '') {
            toast({
                variant: 'success',
                title: 'Başarılı',
                description: value.success,
            });
        }
    },
    { deep: true, immediate: true },
);

const logout = () => {
    window.axios
        .post('/logout')
        .then(() => {
            // Başarılı bir şekilde logout olduktan sonra yönlendirme yap
            window.location.href = '/login'; // Kullanıcıyı login sayfasına yönlendir
        })
        .catch((error) => {
            console.error('Logout işlemi başarısız:', error);
        });
};
</script>

<template>
    <Toaster />
    <div class="container relative max-h-screen min-h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
        <div class="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
            <div class="absolute inset-0 bg-zinc-900" />
            <div class="relative z-20 flex items-start justify-start text-lg font-medium">
                <ApplicationLogo class="w-1/4 text-gray-500" />
            </div>
            <div class="relative z-20 mt-auto">
                <blockquote class="space-y-2">
                    <p class="text-lg">Terminal Kadıköy</p>
                </blockquote>
                <p class="text-white-400 mt-2 text-sm">
                    Sisteme giriş yaparak
                    <a href="/terms-of-use" class="underline underline-offset-4">Kullanım Koşullarımız</a>
                    ve
                    <a href="/privacy-policy" class="underline underline-offset-4">Gizlilik Politikamız</a>
                    'ı kabul etmiş olursunuz.
                </p>
            </div>
            <div class="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]"></div>
        </div>
        <div class="flex h-full items-center justify-center lg:p-8">
            <div class="mx-auto flex min-h-screen w-full flex-col justify-center space-y-6 py-8 sm:w-[450px]">
                <slot />
            </div>
        </div>
        <div class="absolute right-5 top-5">
            <div class="flex items-center">
                <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                        <div class="rounded-lg">
                            <Button variant="ghost" authen>
                                <Sun />
                            </Button>
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent class="w-[--radix-dropdown-menu-trigger-width] min-w-36 rounded-lg" side="bottom" align="end" :side-offset="4">
                        <DropdownMenuLabel class="sr-only p-0 font-normal">
                            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <span>Dark mode</span>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator class="sr-only" />
                        <DropdownMenuGroup>
                            <DropdownMenuItem :as="Button" variant="ghost" class="w-full cursor-pointer justify-start" @click.prevent="changeThemeUser('dark')">
                                <Moon />
                                Dark
                                <span v-if="darkMode == 'dark'" class="ml-auto">
                                    <Check />
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuItem :as="Button" variant="ghost" class="w-full cursor-pointer justify-start" @click.prevent="changeThemeUser('light')">
                                <Sun />
                                Light
                                <span v-if="darkMode == 'light'" class="ml-auto">
                                    <Check />
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuItem :as="Button" variant="ghost" class="w-full cursor-pointer justify-start" @click.prevent="changeThemeUser('auto')">
                                <MonitorCog />
                                Auto
                                <span v-if="darkMode == 'auto'" class="ml-auto">
                                    <Check />
                                </span>
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
                <div v-if="page.props.auth.user">
                    <Button variant="ghost" @click="logout">
                        <LogOut />
                        <span class="sr-only">Logout</span>
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
